import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Camera, Plus, Shirt, Trash2, ChevronRight, Loader2, X, Check, Grid2x2, Video, Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useWardrobeStore } from '../../stores/wardrobeStore';
import { getGarmentImages } from '@/utils/garmentStorage';
import { compressImage } from '@/utils/imageCompression';
import {
  type BodyMediaRecord,
  saveBodyMedia,
  getAllBodyMedia,
  deleteBodyMedia,
  generateVideoThumbnail,
  createBlobUrl,
} from '@/utils/bodyMediaStorage';
import type { Garment } from '../../types';

// ---------------------------------------------------------------------------
// Legacy migration: move old localStorage body images to IndexedDB
// ---------------------------------------------------------------------------
const BODY_IMAGES_KEY = 'vestir_body_images';
const MIGRATION_KEY = 'vestir_body_media_migrated';

async function migrateLegacyImages(): Promise<void> {
  if (localStorage.getItem(MIGRATION_KEY)) return;
  try {
    const raw = localStorage.getItem(BODY_IMAGES_KEY);
    if (raw) {
      const oldImages: { id: string; dataUrl: string; createdAt: number }[] = JSON.parse(raw);
      for (const img of oldImages) {
        await saveBodyMedia({
          id: img.id,
          type: 'image',
          dataUrl: img.dataUrl,
          createdAt: img.createdAt,
        });
      }
      localStorage.removeItem(BODY_IMAGES_KEY);
    }
  } catch { /* best effort */ }
  localStorage.setItem(MIGRATION_KEY, '1');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TryOnPage() {
  const { garments, fetchGarments } = useWardrobeStore();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const preselected = (location.state as { preselectedGarment?: Garment } | null)?.preselectedGarment;

  const [bodyMedia, setBodyMedia] = useState<BodyMediaRecord[]>([]);
  const [selectedMedia, setSelectedMedia] = useState<BodyMediaRecord | null>(null);
  // Object URLs for video playback (need cleanup)
  const [videoUrls, setVideoUrls] = useState<Record<string, string>>({});

  const [selectedGarments, setSelectedGarments] = useState<Garment[]>(preselected ? [preselected] : []);
  const [garmentImages, setGarmentImages] = useState<Record<string, string>>({});
  const [suggestedGarments, setSuggestedGarments] = useState<Garment[]>([]);
  const [tryOnResult, setTryOnResult] = useState<string | null>(null);
  const [tryOnVideoResult, setTryOnVideoResult] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');
  const [showGarmentPicker, setShowGarmentPicker] = useState(false);

  // Load body media from IndexedDB on mount
  useEffect(() => {
    (async () => {
      await migrateLegacyImages();
      const media = await getAllBodyMedia();
      setBodyMedia(media);
    })();
  }, []);

  useEffect(() => { fetchGarments(true); }, []);

  // Load garment thumbnails - resolve idb:// URLs from IndexedDB
  useEffect(() => {
    (async () => {
      const imgs: Record<string, string> = {};
      for (const g of garments) {
        const rawUrl = g.thumbnailUrl || g.imageUrls?.[0];
        if (rawUrl && rawUrl.startsWith('idb://')) {
          const match = rawUrl.match(/^idb:\/\/(.+)\/(\d+)$/);
          if (match) {
            try {
              const stored = await getGarmentImages(match[1]);
              if (stored?.[parseInt(match[2], 10)]) {
                imgs[g.id] = stored[parseInt(match[2], 10)];
                continue;
              }
            } catch { /* fall through */ }
          }
        }
        if (rawUrl && !rawUrl.startsWith('idb://')) {
          imgs[g.id] = rawUrl;
        } else {
          try {
            const stored = await getGarmentImages(g.id);
            if (stored?.[0]) imgs[g.id] = stored[0];
          } catch { /* skip */ }
        }
      }
      setGarmentImages(imgs);
    })();
  }, [garments]);

  // Auto-select first body media
  useEffect(() => {
    if (!selectedMedia && bodyMedia.length > 0) {
      setSelectedMedia(bodyMedia[0]);
    }
  }, [bodyMedia]);

  // Build suggested garments (up to 6 random with images) once images resolve
  useEffect(() => {
    if (garments.length === 0 || Object.keys(garmentImages).length === 0) return;
    const withImages = garments.filter((g) => garmentImages[g.id]);
    const shuffled = [...withImages].sort(() => Math.random() - 0.5);
    setSuggestedGarments(shuffled.slice(0, 6));
  }, [garments, garmentImages]);

  // Cleanup video object URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(videoUrls).forEach(URL.revokeObjectURL);
    };
  }, [videoUrls]);

  const toggleGarment = (g: Garment) => {
    setSelectedGarments((prev) => {
      const isSelected = prev.some((s) => s.id === g.id);
      if (isSelected) return prev.filter((s) => s.id !== g.id);
      return [...prev, g];
    });
    setTryOnResult(null);
    setTryOnVideoResult(null);
  };

  // Get displayable URL for a media item
  const getMediaDisplayUrl = useCallback((media: BodyMediaRecord): string => {
    if (media.type === 'image') return media.dataUrl || '';
    if (media.type === 'video') return media.thumbnailUrl || '';
    return '';
  }, []);

  // Get or create video playback URL
  const getVideoPlaybackUrl = useCallback((media: BodyMediaRecord): string | null => {
    if (media.type !== 'video' || !media.blob) return null;
    if (videoUrls[media.id]) return videoUrls[media.id];
    const url = createBlobUrl(media.blob);
    setVideoUrls((prev) => ({ ...prev, [media.id]: url }));
    return url;
  }, [videoUrls]);

  // ── Upload body image ────────────────────────────────────────

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const fileList = Array.from(files);
    e.target.value = '';

    for (const file of fileList) {
      try {
        const compressed = await compressImage(file, 1024, 0.7);
        const reader = new FileReader();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(compressed);
        });

        const record: BodyMediaRecord = {
          id: `body_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          type: 'image',
          dataUrl,
          createdAt: Date.now(),
        };
        await saveBodyMedia(record);
        setBodyMedia((prev) => [record, ...prev]);
        toast.success('Body photo saved');
      } catch (err) {
        console.error('Failed to process body image:', err);
        toast.error('Failed to upload image');
      }
    }
  };

  // ── Upload body video ────────────────────────────────────────

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const fileList = Array.from(files);
    e.target.value = '';

    for (const file of fileList) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error('Video must be under 100 MB');
        continue;
      }
      try {
        toast.loading('Processing video...', { id: 'video-upload' });
        const thumbnailUrl = await generateVideoThumbnail(file);

        // Get video duration
        const video = document.createElement('video');
        video.preload = 'metadata';
        const duration = await new Promise<number>((resolve) => {
          video.onloadedmetadata = () => resolve(video.duration);
          video.onerror = () => resolve(0);
          video.src = URL.createObjectURL(file);
        });
        URL.revokeObjectURL(video.src);

        const record: BodyMediaRecord = {
          id: `bodyvid_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          type: 'video',
          blob: file,
          thumbnailUrl,
          duration,
          createdAt: Date.now(),
        };
        await saveBodyMedia(record);
        setBodyMedia((prev) => [record, ...prev]);
        toast.success('Body video saved', { id: 'video-upload' });
      } catch (err) {
        console.error('Failed to process body video:', err);
        toast.error('Failed to upload video', { id: 'video-upload' });
      }
    }
  };

  const handleDeleteMedia = async (id: string) => {
    // Revoke any object URL
    if (videoUrls[id]) {
      URL.revokeObjectURL(videoUrls[id]);
      setVideoUrls((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
    await deleteBodyMedia(id);
    setBodyMedia((prev) => {
      const updated = prev.filter((m) => m.id !== id);
      if (selectedMedia?.id === id) {
        setSelectedMedia(updated[0] || null);
      }
      return updated;
    });
  };

  // ── Try-On generation ────────────────────────────────────────

  const resolveGarmentImage = async (garment: Garment): Promise<string> => {
    let garmentImg = garmentImages[garment.id];
    if (!garmentImg) {
      const stored = await getGarmentImages(garment.id);
      garmentImg = stored?.[0] || '';
    }
    if (!garmentImg || garmentImg.startsWith('idb://')) {
      throw new Error('Garment image not available');
    }
    return garmentImg;
  };

  const handleTryOn = async () => {
    if (!selectedMedia || selectedGarments.length === 0) {
      toast.error('Select a body photo/video and at least one garment');
      return;
    }

    setIsGenerating(true);
    setTryOnResult(null);
    setTryOnVideoResult(null);

    try {
      const garment = selectedGarments[0];
      const garmentImg = await resolveGarmentImage(garment);

      if (selectedMedia.type === 'video') {
        await handleVideoTryOn(garmentImg, garment);
      } else {
        await handleImageTryOn(garmentImg, garment);
      }
    } catch (err: any) {
      const msg = err.name === 'AbortError' ? 'Try-on timed out - please try again' : (err.message || 'Try-on failed');
      toast.error(msg);
    } finally {
      setIsGenerating(false);
      setGenerationProgress('');
    }
  };

  const handleImageTryOn = async (garmentImg: string, garment: Garment) => {
    if (!selectedMedia?.dataUrl) throw new Error('No body image');

    setGenerationProgress('Preparing images...');

    const formData = new FormData();

    // Convert body image data URL to blob
    const bodyBlob = await fetch(selectedMedia.dataUrl).then((r) => r.blob());
    formData.append('person_image', bodyBlob, 'person.jpg');

    // Convert garment image to blob - handle both data URLs and regular URLs
    let garmentBlob: Blob;
    if (garmentImg.startsWith('data:')) {
      garmentBlob = await fetch(garmentImg).then((r) => r.blob());
    } else {
      // For regular URLs, fetch with no-cors fallback via a canvas
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Failed to load garment image'));
        el.src = garmentImg;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d')!.drawImage(img, 0, 0);
      garmentBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/jpeg', 0.95);
      });
    }
    formData.append('garment_image', garmentBlob, 'garment.jpg');
    formData.append('category', garment.category || 'upper_body');

    setGenerationProgress('Running AI try-on...');

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 90_000);

    const response = await fetch('/api/mirror/try-on-image', {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });
    clearTimeout(timeout);

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || `Try-on failed: ${response.status}`);
    }
    const data = await response.json();
    if (data.result_image) {
      setTryOnResult(`data:image/png;base64,${data.result_image}`);
      toast.success('Try-on complete!');
    } else {
      throw new Error('No result image returned');
    }
  };

  const handleVideoTryOn = async (garmentImg: string, garment: Garment) => {
    if (!selectedMedia?.blob) throw new Error('No body video');

    setGenerationProgress('Uploading video...');

    const formData = new FormData();
    formData.append('person_video', selectedMedia.blob, 'person.mp4');

    // Convert garment image to blob - handle both data URLs and regular URLs
    let garmentBlob: Blob;
    if (garmentImg.startsWith('data:')) {
      garmentBlob = await fetch(garmentImg).then((r) => r.blob());
    } else {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.crossOrigin = 'anonymous';
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Failed to load garment image'));
        el.src = garmentImg;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d')!.drawImage(img, 0, 0);
      garmentBlob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => b ? resolve(b) : reject(new Error('Canvas toBlob failed')), 'image/jpeg', 0.95);
      });
    }
    formData.append('garment_image', garmentBlob, 'garment.jpg');
    formData.append('category', garment.category || 'upper_body');

    const controller = new AbortController();
    // Video try-on takes longer - 5 minute timeout
    const timeout = setTimeout(() => controller.abort(), 300_000);

    setGenerationProgress('Processing video try-on...');
    const response = await fetch('/api/mirror/try-on-video', {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });
    clearTimeout(timeout);

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.detail || `Video try-on failed: ${response.status}`);
    }

    const data = await response.json();
    if (data.result_video) {
      // result_video is base64 encoded mp4
      setTryOnVideoResult(`data:video/mp4;base64,${data.result_video}`);
      toast.success('Video try-on complete!');
    } else if (data.result_image) {
      // Fallback: server returned image try-on from first frame
      setTryOnResult(`data:image/png;base64,${data.result_image}`);
      toast.success('Try-on complete (single frame)');
    } else {
      throw new Error('No result returned');
    }
  };

  // ── Render ───────────────────────────────────────────────────

  const hasResult = tryOnResult || tryOnVideoResult || isGenerating;

  return (
    <div className="flex flex-col min-h-full pb-[var(--nav-height)] bg-[#FAFAF8]">
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>
          Try On
        </h1>
        <p className="text-sm text-gray-400 mt-1">Upload body photos or videos, pick outfits, see yourself in them</p>
      </div>

      {/* Main content */}
      <div className="flex-1 px-4 space-y-4">

        {/* ── Body Media Section ──────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-700">My Body Photos & Videos</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => videoInputRef.current?.click()}
                className="flex items-center gap-1 text-xs text-[#6C3CE1] font-medium"
              >
                <Video size={14} /> Video
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 text-xs text-[#6C3CE1] font-medium"
              >
                <Plus size={14} /> Photo
              </button>
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleImageUpload}
          />
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={handleVideoUpload}
          />

          {bodyMedia.length === 0 ? (
            <div className="flex gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-1 py-8 border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center gap-2 hover:border-[#6C3CE1]/30 transition-colors"
              >
                <Camera size={28} className="text-gray-300" />
                <span className="text-sm text-gray-400">Upload photo</span>
              </button>
              <button
                onClick={() => videoInputRef.current?.click()}
                className="flex-1 py-8 border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center gap-2 hover:border-[#6C3CE1]/30 transition-colors"
              >
                <Video size={28} className="text-gray-300" />
                <span className="text-sm text-gray-400">Upload video</span>
              </button>
            </div>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {bodyMedia.map((media) => (
                <div
                  key={media.id}
                  className={`relative flex-shrink-0 w-20 h-28 rounded-xl overflow-hidden cursor-pointer border-2 transition-all ${
                    selectedMedia?.id === media.id
                      ? 'border-[#6C3CE1] shadow-md'
                      : 'border-transparent hover:border-gray-200'
                  }`}
                  onClick={() => setSelectedMedia(media)}
                >
                  <img
                    src={getMediaDisplayUrl(media)}
                    alt={media.type === 'video' ? 'Body video' : 'Body photo'}
                    className="w-full h-full object-cover"
                  />
                  {/* Video badge */}
                  {media.type === 'video' && (
                    <div className="absolute top-1 left-1 bg-black/60 rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
                      <Play size={8} className="text-white fill-white" />
                      <span className="text-[8px] text-white">
                        {media.duration ? `${Math.round(media.duration)}s` : 'vid'}
                      </span>
                    </div>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteMedia(media.id); }}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center"
                  >
                    <X size={10} className="text-white" />
                  </button>
                  {selectedMedia?.id === media.id && (
                    <div className="absolute bottom-0 left-0 right-0 bg-[#6C3CE1] text-white text-[9px] text-center py-0.5">
                      Selected
                    </div>
                  )}
                </div>
              ))}
              {/* Add more buttons */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="flex-shrink-0 w-20 h-28 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 hover:border-[#6C3CE1]/30 transition-colors"
              >
                <Camera size={16} className="text-gray-300" />
                <span className="text-[9px] text-gray-400">Photo</span>
              </button>
              <button
                onClick={() => videoInputRef.current?.click()}
                className="flex-shrink-0 w-20 h-28 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 hover:border-[#6C3CE1]/30 transition-colors"
              >
                <Video size={16} className="text-gray-300" />
                <span className="text-[9px] text-gray-400">Video</span>
              </button>
            </div>
          )}
        </div>

        {/* ── Garments to Try On ────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-gray-700">
              Garments to Try On
              {selectedGarments.length > 0 && (
                <span className="ml-1.5 text-xs font-normal text-[#6C3CE1]">
                  {selectedGarments.length} selected
                </span>
              )}
            </h2>
          </div>

          {suggestedGarments.length === 0 && garments.length === 0 ? (
            <button
              onClick={() => setShowGarmentPicker(true)}
              className="w-full py-6 border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center gap-2 hover:border-[#6C3CE1]/30 transition-colors"
            >
              <Shirt size={24} className="text-gray-300" />
              <span className="text-sm text-gray-400">No garments yet - add items in your Closet</span>
            </button>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
              {suggestedGarments.map((g) => {
                const isSelected = selectedGarments.some((s) => s.id === g.id);
                return (
                  <button
                    key={g.id}
                    onClick={() => toggleGarment(g)}
                    className={`relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      isSelected
                        ? 'border-[#6C3CE1] shadow-md'
                        : 'border-transparent hover:border-gray-200'
                    }`}
                  >
                    <img
                      src={garmentImages[g.id] || ''}
                      alt={g.subCategory}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1">
                      <p className="text-[9px] text-white capitalize truncate">{g.subCategory}</p>
                    </div>
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 bg-[#6C3CE1] rounded-full flex items-center justify-center">
                        <Check size={10} className="text-white" />
                      </div>
                    )}
                  </button>
                );
              })}
              {/* Open Closet button */}
              <button
                onClick={() => setShowGarmentPicker(true)}
                className="flex-shrink-0 w-20 h-20 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 hover:border-[#6C3CE1]/30 transition-colors"
              >
                <Grid2x2 size={18} className="text-gray-300" />
                <span className="text-[9px] text-gray-400">All Items</span>
              </button>
            </div>
          )}
        </div>

        {/* ── Try-On Result ────────────────────────────────────── */}
        {hasResult && (
          <div>
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Result</h2>
            <div className="bg-white rounded-2xl border overflow-hidden">
              {isGenerating ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 size={32} className="text-[#6C3CE1] animate-spin mb-3" />
                  <p className="text-sm text-gray-400">{generationProgress || 'Generating try-on...'}</p>
                  <p className="text-xs text-gray-300 mt-1">
                    {selectedMedia?.type === 'video'
                      ? 'Video try-on may take 1-3 minutes'
                      : 'This may take a few seconds'}
                  </p>
                </div>
              ) : tryOnVideoResult ? (
                <video
                  src={tryOnVideoResult}
                  controls
                  autoPlay
                  loop
                  playsInline
                  className="w-full"
                />
              ) : tryOnResult ? (
                <img src={tryOnResult} alt="Try-on result" className="w-full" />
              ) : null}
            </div>
          </div>
        )}

        {/* ── Try On Button ────────────────────────────────────── */}
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={handleTryOn}
          disabled={!selectedMedia || selectedGarments.length === 0 || isGenerating}
          className={`w-full py-3.5 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors ${
            selectedMedia && selectedGarments.length > 0 && !isGenerating
              ? 'bg-[#6C3CE1] text-white'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isGenerating ? (
            <><Loader2 size={18} className="animate-spin" /> Generating...</>
          ) : selectedMedia?.type === 'video' ? (
            <><Video size={18} /> Try On Video</>
          ) : (
            <><Shirt size={18} /> Try On</>
          )}
        </motion.button>
      </div>

      {/* ── Garment Picker Modal ──────────────────────────────── */}
      <AnimatePresence>
        {showGarmentPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-end"
            onClick={() => setShowGarmentPicker(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-lg mx-auto bg-white rounded-t-3xl max-h-[70vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-semibold">
                  Choose Garments
                  {selectedGarments.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-[#6C3CE1]">
                      {selectedGarments.length} selected
                    </span>
                  )}
                </h3>
                <button onClick={() => setShowGarmentPicker(false)}>
                  <X size={20} className="text-gray-400" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {garments.length === 0 ? (
                  <p className="text-center text-gray-400 py-8">
                    No garments yet. Add items in your Closet first.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {garments.filter((g) => garmentImages[g.id]).map((g) => {
                      const isSelected = selectedGarments.some((s) => s.id === g.id);
                      return (
                        <button
                          key={g.id}
                          onClick={() => toggleGarment(g)}
                          className={`relative rounded-xl overflow-hidden border-2 aspect-square transition-all ${
                            isSelected
                              ? 'border-[#6C3CE1]'
                              : 'border-transparent hover:border-gray-200'
                          }`}
                        >
                          <img
                            src={garmentImages[g.id] || ''}
                            alt={g.subCategory}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                            <p className="text-[10px] text-white capitalize truncate">{g.subCategory}</p>
                          </div>
                          {isSelected && (
                            <div className="absolute top-1 right-1 w-5 h-5 bg-[#6C3CE1] rounded-full flex items-center justify-center">
                              <Check size={10} className="text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {selectedGarments.length > 0 && (
                <div className="p-4 border-t">
                  <button
                    onClick={() => setShowGarmentPicker(false)}
                    className="w-full py-2.5 bg-[#6C3CE1] text-white rounded-xl text-sm font-medium"
                  >
                    Done ({selectedGarments.length} selected)
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
